/**
 * OCRProvider Interface
 *
 * All OCR providers must implement this contract.
 * The ERP depends only on OCRService, never on a specific provider.
 *
 * Future providers: Google Vision, Azure OCR, AWS Textract, PaddleOCR, Tesseract
 */

/**
 * @typedef {Object} OCRRawResult
 * @property {string|null} proveedor_texto   - Raw supplier text extracted
 * @property {string|null} cif_texto         - Raw CIF/NIF text extracted
 * @property {string|null} fecha_texto       - Raw date text extracted
 * @property {string|null} numero_factura    - Invoice number text
 * @property {string[]}    lineas_texto      - Array of raw line strings
 * @property {string|null} total_texto       - Raw total text
 * @property {string}      texto_completo    - Full raw text from document
 * @property {string}      provider          - Provider name used
 * @property {number}      confidence        - 0-100 confidence score
 * @property {string[]}    warnings          - Non-fatal warnings
 */

/**
 * @typedef {Object} OCRProviderConfig
 * @property {string}  apiKey   - Provider API key
 * @property {number}  timeout  - Request timeout in ms (default 60000)
 * @property {string}  language - Language hint (e.g. 'spa', 'eng')
 */

/**
 * Base class / interface for OCR providers.
 * Extend this class to add a new provider.
 */
export class OCRProvider {
  /**
   * @param {OCRProviderConfig} config
   */
  constructor(config = {}) {
    if (this.constructor === OCRProvider) {
      throw new Error('OCRProvider is an abstract class. Extend it to create a provider.');
    }
    this.config = {
      apiKey: config?.apiKey || '',
      timeout: config?.timeout || 60000,
      language: config?.language || 'spa',
    };
  }

  /**
   * Extract raw text from a file (PDF or image).
   * Must be implemented by every provider.
   *
   * @param {File|Blob} file         - The file to process
   * @param {string}    mimeType     - MIME type of the file
   * @returns {Promise<OCRRawResult>}
   */
  // eslint-disable-next-line no-unused-vars
  async extractText(file, mimeType) {
    throw new Error(`extractText() must be implemented by ${this.constructor.name}`);
  }

  /**
   * Returns the provider identifier string.
   * @returns {string}
   */
  getProviderName() {
    throw new Error(`getProviderName() must be implemented by ${this.constructor.name}`);
  }

  /**
   * Validates that the provider is properly configured.
   * @returns {{ valid: boolean, error?: string }}
   */
  validate() {
    if (!this.config?.apiKey) {
      return { valid: false, error: `${this.getProviderName()}: API Key no configurada` };
    }
    return { valid: true };
  }
}

export default OCRProvider;
